import React, { useContext } from 'react';
import {
  deepClone,
  ImpersonationContext,
  fsDocRef,
  fsWriteBatch,
  AccessibilityContext,
} from '@monash/portal-frontend-common';
import { nanoid } from 'nanoid';
import { PageContext } from '@/components/providers/page-provider/PageProvider';
import { DataContext } from '@/components/providers/data-provider/DataProvider';
import { useSnackbar } from '@/components/providers/SnackbarProvider';
import { formatDefaultData } from './utils';
import WidgetListItem from './WidgetListItem';
import { useAdditionalOptions } from '@/hooks/use-additional-options';

const AddWidgetTrigger = ({
  widget,
  setNewWidgetId,
  pageId,
  closeMenus,
  setWidgetOrder,
  scrollToHeader,
  modalIsOpen,
  innerRef,
}) => {
  const { addSnackbar } = useSnackbar();

  const typeId = Number(widget[0]);
  const WidgetModule = widget[1];

  // Click to add a new widget
  const { pagesData } = useContext(PageContext);
  const { resetAppLiveMsgs } = useContext(AccessibilityContext);
  const { currentUser } = useContext(ImpersonationContext);
  const { setPortalPreferences } = useContext(DataContext);
  const additionalOptions = useAdditionalOptions(
    WidgetModule?.additionalOptions
  );

  const addWidget = async () => {
    closeMenus();
    scrollToHeader();

    const batch = fsWriteBatch();
    const newWidgetId = nanoid();

    if (additionalOptions) {
      const data = formatDefaultData(additionalOptions);
      const widgetDoc = fsDocRef(
        `users/${currentUser.uid}/widgets/${newWidgetId}`
      );

      data.typeId = typeId; // add type id to widget doc for easier database querying

      batch.set(widgetDoc, data);
    }

    // send widget ID back to render widget function
    setNewWidgetId(newWidgetId);

    const preferencesDoc = fsDocRef(`users/${currentUser.uid}`);

    const newWidget = {
      size: WidgetModule?.defaultSize || 'L',
      typeId,
    };

    const newWidgetOrder = [
      newWidgetId,
      ...pagesData.customPages[pageId].widgetOrder,
    ];

    batch.update(preferencesDoc, {
      [`preferences.pages.widgets.${newWidgetId}`]: newWidget,
      [`preferences.pages.customPages.${pageId}.widgetOrder`]: newWidgetOrder,
    });

    batch
      .commit()
      .then(() => {
        // update pages state - add new widget to widget list
        const newPages = deepClone(pagesData);
        newPages.widgets = { ...newPages.widgets, [newWidgetId]: newWidget };
        newPages.customPages[pageId].widgetOrder = newWidgetOrder;
        handleUpdateSuccess(newPages);
      })
      .catch(handleUpdateError);
  };

  const handleUpdateSuccess = (newPages) => {
    resetAppLiveMsgs();
    setWidgetOrder(newPages.customPages[pageId].widgetOrder);
    setPortalPreferences((f) => {
      addSnackbar({
        message: `${WidgetModule?.name} widget has been added.`,
        type: 'success',
      });
      return { ...f, pages: newPages };
    });
  };

  const handleUpdateError = (error) => {
    resetAppLiveMsgs();
    addSnackbar({
      message:
        "We're not able to add widgets right now – please try again later",
      type: 'error',
    });
    console.warn(
      '[updatePortalPreferences]: api call error, failed to add widget',
      error
    );
  };

  return (
    <WidgetListItem
      widget={widget}
      onClick={modalIsOpen ? addWidget : () => {}}
      innerRef={innerRef}
      role="menuitem"
      data-wgt-type={typeId}
    />
  );
};

export default AddWidgetTrigger;
